import { createApiMutationHook } from '../../common/queryClient/createApiMutationHook';
import {
  getMutationFn,
  HttpHeaders,
} from '../../common/queryClient/getMutationFn';
import { apiClient } from '../../common/ApiClient';
import { expand } from 'uri-template-lite';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import { ObjectLockUrl } from './ObjectLockUrl';
import { ObjectType } from '../types';

export interface ObjectLockId {
  objectId: number;
  objectType: ObjectType;
  discriminator?: string;
}

export interface ObjectLockRequest {
  lockId: ObjectLockId;
  additionalLocks?: ObjectLockId[];
  checkLocks?: ObjectLockId[];
}

export interface ObjectLock {
  objectId: number;
  objectType: ObjectType;
  discriminator?: string;
  timestamp: string;
  expiry: string;
  username: string;
  firstName: string;
  lastName: string;
}

export interface LockResponse {
  locked: boolean;
  owner: boolean | null;
  lockId: ObjectLockId;
  lock: ObjectLock;
}

const lockObject = (body: ObjectLockRequest) =>
  apiClient
    .post<LockResponse>(ObjectLockUrl.CREATE, body)
    .then((response) => response.data);

const extendLock = (objectId: number, body: Partial<ObjectLockRequest>) =>
  apiClient
    .patch<LockResponse>(expand(ObjectLockUrl.LOCK, { ...body.lockId }), body)
    .then((response) => response.data);

const releaseLock = (objectId: number, headers?: HttpHeaders) =>
  apiClient
    .delete<void>(
      expand(ObjectLockUrl.LOCK, {
        objectId,
        objectType: headers?.objectType,
        discriminator: headers?.discriminator,
      }),
      {
        headers: {
          additionalLocks: headers?.additionalLocks,
        },
      }
    )
    .then((response) => response.data);

const getLock = (lock: ObjectLockId | ObjectLockRequest) => {
  let lockId: ObjectLockId;
  const headers: HttpHeaders = {};
  if ('lockId' in lock) {
    lockId = lock.lockId;
    if (lock.additionalLocks) {
      headers.additionalLocks = lock.additionalLocks.map((lock) =>
        [lock.objectType, lock.objectId, lock.discriminator]
          .filter((part) => !!part)
          .join('-')
      );
    }
  } else {
    lockId = lock;
  }

  return apiClient
    .get<LockResponse>(expand(ObjectLockUrl.LOCK, lockId), { headers })
    .then((response) => response.data);
};

export const useLockMutation = createApiMutationHook(
  ['object-lock-mutation'],
  getMutationFn(lockObject, extendLock, releaseLock)
);

export const useObjectLockQuery = createApiQueryHook(['object-lock'], getLock);
